import { type FC, useState } from 'react';

import {
  useCreateMilestoneStep,
  useGrowthPlan,
  useGrowthPlanSteps,
  useRefreshSuggestedMilestoneStep,
} from '@/hooks';
import { AppMessageSeverities } from '@/types';
import {
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Skeleton,
  Typography,
} from '@/ui';
import { noop } from '@/utils';

import { api } from '@/store/api';
import { useAppDispatch } from '@/store/hooks';
import { setAppMessage } from '@/store/slices/appSlice';

interface SuggestionProps {
  color: string;
  hasSuggestionRefreshing?: boolean;
  index: number;
  loading?: boolean;
  milestoneId: string;
  setHasSuggestionRefreshing?: (value: boolean) => void;
  suggestion: string;
}

const Suggestion: FC<SuggestionProps> = ({
  color,
  index,
  loading,
  milestoneId,
  suggestion,
  hasSuggestionRefreshing,
  setHasSuggestionRefreshing = noop,
}) => {
  const [suggestionText, setSuggestionText] = useState(suggestion);
  const [failedSuggestion, setFailedSuggestion] = useState(false);
  const dispatch = useAppDispatch();
  const createMilestoneStep = useCreateMilestoneStep();
  const refreshSuggestedMilestoneStep = useRefreshSuggestedMilestoneStep();

  const { plan, refetch } = useGrowthPlan();
  const { refetch: refetchSteps } = useGrowthPlanSteps();

  const refreshSuggestion = () =>
    refreshSuggestedMilestoneStep.mutate(
      { index, milestoneId },
      {
        onError: () => {
          dispatch(
            setAppMessage({
              message: 'Sorry, the suggestion could not be generated',
              open: true,
              severity: AppMessageSeverities.Error,
            }),
          );
          setFailedSuggestion(true);
          setHasSuggestionRefreshing(false);
        },
        onSuccess: (response) => {
          setSuggestionText(response[0] ?? '');
          setHasSuggestionRefreshing(false);
        },
      },
    );

  const onSuccess = () => {
    refetch();
    refetchSteps();
    dispatch(
      api.util.invalidateTags(['GrowthPlan', 'Milestone', 'MilestoneStep']),
    );
    refreshSuggestion();
  };

  const handleAddClick = () => {
    setHasSuggestionRefreshing(true);
    createMilestoneStep.mutate(
      {
        growthPlanId: plan.id,
        milestoneId,
        suggested: true,
        title: suggestionText,
      },
      {
        onSuccess,
      },
    );
  };

  const handleRefreshClick = () => {
    setFailedSuggestion(false);
    setHasSuggestionRefreshing(true);
    refreshSuggestion();
  };

  const textColor =
    color === 'sunny' ? `text-${color}-600` : `text-${color}-500`;
  const bgColor = `bg-${color}-300`;
  const borderColor = `border-${color}-300`;

  if (failedSuggestion) {
    return (
      <div
        className={`flex w-full items-center border-0 border-b border-solid ${borderColor} gap-2 pb-2 pl-1 last:border-b-0 last:pb-0`}
      >
        <Typography className="ml-2 flex-1" variant="body1">
          Sorry, the suggestion could not be generated. Please try again.
        </Typography>
        <IconButton
          disabled={hasSuggestionRefreshing}
          onClick={handleRefreshClick}
        >
          <Icon>refresh</Icon>
        </IconButton>
      </div>
    );
  }

  if (loading || refreshSuggestedMilestoneStep.isLoading)
    return (
      <div
        className={`flex w-full items-center border-0 border-b border-solid ${borderColor} gap-2 pb-2 pl-1 last:border-b-0 last:pb-0`}
      >
        <CircularProgress size={24} />
        <Skeleton className="ml-2" height={42} width="40%" />
      </div>
    );

  return (
    <div
      className={`flex w-full items-center border-0 border-b border-solid ${borderColor} gap-2 pb-2 pl-1 last:border-b-0 last:pb-0`}
    >
      <Icon className={`rounded ${bgColor} ${textColor} h-4.5 w-4.5`}>
        magic_button
      </Icon>
      <Typography className="ml-2 flex-1" variant="body1">
        {suggestionText}
      </Typography>
      <IconButton
        disabled={hasSuggestionRefreshing}
        size="small"
        onClick={handleRefreshClick}
      >
        <Icon>refresh</Icon>
      </IconButton>
      <Button
        disabled={hasSuggestionRefreshing}
        size="small"
        variant="outlined"
        onClick={handleAddClick}
      >
        Add
      </Button>
    </div>
  );
};
export default Suggestion;
